/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import { Text } from '@tremor/react'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { getRateMetrics, RateMetricsParams, RateMetricsResponse, RateMetricData } from './api'

interface Store {
  id: number
  name: string
}

interface RateMetricsProps {
  period?: { month: number; year: number }
  storeIds?: number[]
  stores?: Store[]
  trigger?: number
  onLoaded?: () => void
}

const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }

const RateMetrics: React.FC<RateMetricsProps> = ({
  period,
  storeIds,
  stores,
  trigger,
  onLoaded,
}) => {
  const [data, setData] = useState<{ [storeId: number]: RateMetricData[] }>({})
  const [error, setError] = useState<string | null>(null)
  const [isLoadingData] = useState(false)

  const [apiCall, apiLoading] = useApiCall<RateMetricsParams, RateMetricsResponse>(getRateMetrics)

  // Track loading state
  useLoadingTracker({ isLoading: apiLoading, componentId: 'RateMetrics' })

  const fetchData = async () => {
    try {
      const endDate = moment()
        .year(period?.year ?? moment().year())
        .month(period?.month ?? moment().month())
        .endOf('month')
        .format('YYYY-MM-DD')

      const response = await apiCall({
        endDate,
        storeId: storeIds && storeIds.length > 0 ? storeIds[0] : undefined,
        comparisonStoreId: storeIds && storeIds.length > 1 ? storeIds[1] : undefined,
      })

      if (response?.data) {
        setData({ 0: response.data })
        setError(null)
      }
    } catch (apiError) {
      console.error('Error fetching rate metrics data:', apiError)
      setError('Error al cargar métricas de tasa')
    } finally {
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger && trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const getStoreName = (storeId: number) => {
    if (storeId === 0) return 'Todos los locales'
    const store = stores?.find(s => s.id === storeId)
    return store?.name || `Store ${storeId}`
  }

  const isLoading = apiLoading || isLoadingData

  const renderMetricsContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <CircularProgress size={40} />
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-red-500 text-center" style={montserratStyle}>
            {error}
          </div>
        </div>
      )
    }

    if (!data[0]) {
      return (
        <div className="flex items-center justify-center h-full">
          <Text className="text-center">
            No hay datos disponibles para mostrar en este momento.
          </Text>
        </div>
      )
    }

    const metricsData = data[0]
    const comparisonStoreId = storeIds && storeIds.length > 1 ? storeIds[1] : undefined

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {metricsData.map(metric => (
          <div key={metric.title} className="bg-white rounded-xl shadow p-6">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-600 mb-2" style={montserratStyle}>
                {metric.title}
              </span>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl font-bold" style={montserratStyle}>
                  {metric.value}%
                </span>
                {metric.comparisonValue !== undefined && metric.diff !== undefined && (
                  <span
                    className={`font-bold rounded-lg px-2 py-1 flex items-center text-sm ${
                      metric.isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}
                    style={montserratStyle}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={metric.isPositive ? '#47B960' : '#EF4444'}
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                      style={{ transform: metric.isPositive ? 'none' : 'rotate(180deg)' }}
                    >
                      <path d="M6 15l6-6 6 6" />
                    </svg>
                    {Math.abs(metric.diff)}%
                  </span>
                )}
              </div>
              {metric.comparisonValue !== undefined && comparisonStoreId && (
                <span className="text-gray-600 text-sm" style={montserratStyle}>
                  ({metric.comparisonValue}% de {getStoreName(comparisonStoreId)})
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return renderMetricsContent()
}

export default RateMetrics
